import { Draft, PayloadAction, createSlice } from '@reduxjs/toolkit';
import { v4 as uuid } from 'uuid';

import * as ConnectionActions from 'src/ui/features/connection';
import {
  createPartnerConnection,
  deletePartnerConnectionDetail,
  deletePartnerDataSource,
  editPartnerConnection,
} from 'src/ui/features/partnerconnection';
import { Connection as GraphConnection, VoiceboxApp } from 'src/ui/graph/types';

export const enum DialogType {
  PARTNER_CONNECTION = 'PARTNER_CONNECTION',
  UPDATE_PROFILE = 'UPDATE_PROFILE',
  CREATE_PROFILE = 'CREATE_PROFILE',
  VERIFY_EMAIL = 'VERIFY_EMAIL',
  UNSUPPORTED_BROWSER = 'UNSUPPORTED_BROWSER',
  CREATE_API_TOKEN = 'CREATE_API_TOKEN',
  CREATE_VOICEBOX_APP = 'CREATE_VOICEBOX_APP',
  UPDATE_VOICEBOX_APP = 'UPDATE_VOICEBOX_APP',
  DELETE_ACCOUNT = 'DELETE_ACCOUNT',
  ADD_ORGANIZATION = 'ADD_ORGANIZATION',
  CONFIGURE_ORGANIZATION_SSO = 'CONFIGURE_ORGANIZATION_SSO',
  DELETE_ORGANIZATION = 'DELETE_ORGANIZATION',
  EDIT_ORGANIZATION = 'EDIT_ORGANIZATION',
  INVITE_ORGANIZATION_MEMBERS = 'INVITE_ORGANIZATION_MEMBERS',
  LEAVE_ORGANIZATION = 'LEAVE_ORGANIZATION',
  TRANSFER_ORGANIZATION = 'TRANSFER_ORGANIZATION',
  SIGN_IN = 'SIGN_IN',
  ADD_ENDPOINT = 'ADD_ENDPOINT',
  ADD_SSO_ENDPOINT = 'ADD_SSO_ENDPOINT',
  EDIT_ENDPOINT = 'EDIT_ENDPOINT',
  DELETE_ENDPOINT = 'DELETE_ENDPOINT',
  CANCEL_ENDPOINT = 'CANCEL_ENDPOINT',
  VOICEBOX_RESPONSE_REPORT = 'VOICEBOX_RESPONSE_REPORT',
  ADD_MY_ORGANIZATION_ENDPOINT = 'ADD_MY_ORGANIZATION_ENDPOINT',
  EDIT_MY_ORGANIZATION_ENDPOINT = 'EDIT_MY_ORGANIZATION_ENDPOINT',
  DELETE_MY_ORGANIZATION_ENDPOINT = 'DELETE_MY_ORGANIZATION_ENDPOINT',
  RENAME_MY_SHARED_ENDPOINT = 'RENAME_MY_SHARED_ENDPOINT',
  SHARE_ORGANIZATION_ENDPOINT = 'SHARE_ORGANIZATION_ENDPOINT',
  EDIT_SHARED_ORGANIZATION_ENDPOINT = 'EDIT_SHARED_ORGANIZATION_ENDPOINT',
  SHARE_PERSONAL_ENDPOINT = 'SHARE_PERSONAL_ENDPOINT',
  EDIT_SHARED_PERSONAL_ENDPOINT = 'EDIT_SHARED_PERSONAL_ENDPOINT',
}

export const enum NavbarAsideType {
  PORTAL_ENDPOINT = 'PORTAL_ENDPOINT',
  VOICEBOX_CHAT_LIST = 'VOICEBOX_CHAT_LIST',
  VOICEBOX_SETTINGS = 'VOICEBOX_SETTINGS',
}

type CreateApiTokenDialogData = {
  appId?: string;
};

type UpdateVoiceboxAppDialogData = {
  app: VoiceboxApp;
};

type EditSharedOrganizationEndpointDialogData = {
  userId: string;
};

type VoiceboxResponseReportDialogData = {
  connection: GraphConnection;
};

type DialogData =
  | { type: DialogType; data?: never }
  | { type: DialogType.CREATE_API_TOKEN; data: CreateApiTokenDialogData }
  | {
      type: DialogType.UPDATE_VOICEBOX_APP;
      data: UpdateVoiceboxAppDialogData;
    }
  | {
      type: DialogType.EDIT_SHARED_ORGANIZATION_ENDPOINT;
      data: EditSharedOrganizationEndpointDialogData;
    }
  | {
      type: DialogType.EDIT_SHARED_PERSONAL_ENDPOINT;
      data: EditSharedOrganizationEndpointDialogData;
    }
  | {
      type: DialogType.VOICEBOX_RESPONSE_REPORT;
      data: VoiceboxResponseReportDialogData;
    };

const persistentDialogs = new Set<DialogType | null>([
  DialogType.CREATE_PROFILE,
  DialogType.CREATE_VOICEBOX_APP,
  DialogType.UNSUPPORTED_BROWSER,
]);

const queueableDialogs = new Set<DialogType>([
  DialogType.SIGN_IN,
  DialogType.CREATE_PROFILE,
  DialogType.UNSUPPORTED_BROWSER,
]);

export interface UiState {
  acknowledgedUnsupportedBrowser: boolean;
  dialog: DialogData | null;
  initialVisit: boolean;
  loggedinUserEmail: string | null;
  navbar: {
    aside: NavbarAsideType | null;
    isAsideOpen: boolean;
    isExpanded: boolean;
  };
  nextDialogQueue: DialogData[];
  unsupportedBrowser: boolean;
  userId: string | null;
  verifiedUser: boolean;
  voicebox: {
    isDrawerOpen: boolean;
    newConversationKey: string;
  };
}

const initialState = (): UiState => ({
  acknowledgedUnsupportedBrowser: false,
  dialog: null,
  initialVisit: true,
  loggedinUserEmail: null,
  navbar: {
    aside: null,
    isAsideOpen: false,
    isExpanded: true,
  },
  nextDialogQueue: [],
  unsupportedBrowser: false,
  userId: null,
  verifiedUser: true,
  voicebox: {
    isDrawerOpen: false,
    newConversationKey: '',
  },
});

const openOrQueueDialog = (
  state: Draft<UiState>,
  dialog: DialogData,
  force?: boolean
) => {
  if (state.dialog && force) {
    state.nextDialogQueue.push(state.dialog);
    state.dialog = dialog;
  } else if (state.dialog !== null && queueableDialogs.has(dialog.type)) {
    const isDialogQueued =
      state.dialog.type === dialog.type ||
      state.nextDialogQueue.find(
        /* istanbul ignore next */
        (nextDialog) => nextDialog.type === dialog.type
      );
    if (!isDialogQueued) {
      state.nextDialogQueue.push(dialog);
    }
  } else {
    state.dialog = dialog;
  }
};

export const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    closedDialog: (state) => {
      if (!state.nextDialogQueue.length) {
        state.dialog = null;
      } else {
        const nextDialogType = state.nextDialogQueue.shift();
        state.dialog = nextDialogType || null;
      }
    },
    openedDialog: (state, action: PayloadAction<DialogData>) => {
      openOrQueueDialog(state, action.payload);
    },
    profileUpdated: (state) => {
      state.initialVisit = false;
      state.dialog = null;
    },
    returningUser: (state) => {
      state.initialVisit = false;
    },
    unverifiedUser: (state) => {
      state.verifiedUser = false;
    },
    usingUnsupportedBrowser: (state) => {
      state.unsupportedBrowser = true;
    },
    acknowledgeUsingUnsupportedBrowser: (state) => {
      state.acknowledgedUnsupportedBrowser = true;
    },
    showInitialDialog: (state) => {
      if (state.initialVisit) {
        openOrQueueDialog(state, { type: DialogType.CREATE_PROFILE });
      }
    },
    showSignInDialog: (state, action: PayloadAction<boolean | undefined>) => {
      openOrQueueDialog(state, { type: DialogType.SIGN_IN }, action.payload);
    },
    showVerifyEmailDialog: (state) => {
      state.dialog = { type: DialogType.VERIFY_EMAIL };
      openOrQueueDialog(state, { type: DialogType.VERIFY_EMAIL });
    },
    showUnsupportedBrowserDialog: (state) => {
      if (state.unsupportedBrowser && !state.acknowledgedUnsupportedBrowser) {
        openOrQueueDialog(state, { type: DialogType.UNSUPPORTED_BROWSER });
      }
    },
    showCreateApiTokenDialog: (
      state,
      action: PayloadAction<CreateApiTokenDialogData>
    ) => {
      openOrQueueDialog(state, {
        type: DialogType.CREATE_API_TOKEN,
        data: action.payload,
      });
    },
    showCreateVoiceboxAppDialog: (state) => {
      openOrQueueDialog(state, { type: DialogType.CREATE_VOICEBOX_APP });
    },
    showUpdateVoiceboxAppDialog: (
      state,
      action: PayloadAction<UpdateVoiceboxAppDialogData>
    ) => {
      openOrQueueDialog(state, {
        type: DialogType.UPDATE_VOICEBOX_APP,
        data: action.payload,
      });
    },
    startedNewVoiceboxConversation: (state) => {
      state.voicebox.newConversationKey = uuid();
    },
    toggledNavbarAside: (
      state,
      action: PayloadAction<{
        aside?: NavbarAsideType | null;
        isAsideOpen: boolean;
      }>
    ) => {
      state.navbar.aside = action.payload.aside ?? state.navbar.aside;
      state.navbar.isAsideOpen = action.payload.isAsideOpen;
    },
    toggledNavbarExpanded: (state, action: PayloadAction<boolean>) => {
      state.navbar.isExpanded = action.payload;
    },
    toggledVoiceboxDrawer: (state, action: PayloadAction<boolean>) => {
      state.voicebox.isDrawerOpen = action.payload;
    },
    loggedinUserEmail: (state, action: PayloadAction<string | null>) => {
      state.loggedinUserEmail = action.payload;
    },
    userId: (state, action: PayloadAction<string | null>) => {
      state.userId = action.payload;
    },
  },
  extraReducers: (builder) => {
    // When connection is cleared we need to close any open dialog
    builder.addCase(ConnectionActions.clearConnection.type, (state) => {
      if (state.dialog && persistentDialogs.has(state.dialog.type)) {
        return;
      }

      state.dialog = null;
    });
    builder.addCase(createPartnerConnection.type, (state) => {
      state.dialog = { type: DialogType.PARTNER_CONNECTION };
    });
    builder.addCase(editPartnerConnection.type, (state) => {
      state.dialog = { type: DialogType.PARTNER_CONNECTION };
    });
    builder.addCase(deletePartnerDataSource.type, (state) => {
      state.dialog = { type: DialogType.PARTNER_CONNECTION };
    });
    builder.addCase(deletePartnerConnectionDetail.type, (state) => {
      state.dialog = { type: DialogType.PARTNER_CONNECTION };
    });
  },
});

export const {
  closedDialog,
  openedDialog,
  profileUpdated,
  returningUser,
  showInitialDialog,
  showSignInDialog,
  showVerifyEmailDialog,
  showUnsupportedBrowserDialog,
  showCreateApiTokenDialog,
  showCreateVoiceboxAppDialog,
  showUpdateVoiceboxAppDialog,
  startedNewVoiceboxConversation,
  toggledNavbarAside,
  toggledNavbarExpanded,
  toggledVoiceboxDrawer,
  usingUnsupportedBrowser,
  acknowledgeUsingUnsupportedBrowser,
  unverifiedUser,
  userId,
  loggedinUserEmail,
} = uiSlice.actions;

export const { getInitialState } = uiSlice;
